import { GlobeAltIcon } from '@heroicons/react/24/outline';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Agents as AgentsIcon,
  Assets as AssetsIcon,
  Seeds as SeedsIcon,
  Vulnerabilities as VulnerabilitiesIcon,
} from '@/components/chariot-ui';

import { type ExportType, getEntityLabel } from './exportUtils';

interface ExportToastProps {
  entityType: ExportType;
  toastId: string | number;
}

// eslint-disable-next-line react-refresh/only-export-components
function EntityIcon({ type }: { type: ExportType }) {
  const className = 'size-5 shrink-0';
  switch (type) {
    case 'asset':
      return (
        <AssetsIcon className={`${className} stroke-chariot-text-primary`} />
      );
    case 'seed':
      return <SeedsIcon className={`${className} fill-chariot-text-primary`} />;
    case 'risk':
      return (
        <VulnerabilitiesIcon
          className={`${className} fill-chariot-text-primary`}
        />
      );
    case 'technology':
      return (
        <GlobeAltIcon
          className={`${className} text-chariot-text-primary stroke-chariot-text-primary`}
        />
      );
    case 'agent':
      return (
        <AgentsIcon
          className={`${className} fill-chariot-text-primary text-chariot-text-primary`}
        />
      );
  }
}

/**
 * ExportToast - Custom toast component shown when an export starts.
 *
 * After 4 seconds, animates toward the download icon (data-testid="global-download-icon").
 * With prefers-reduced-motion, simply fades out.
 */
// eslint-disable-next-line react-refresh/only-export-components
function ExportToast({ entityType, toastId }: ExportToastProps) {
  const [phase, setPhase] = useState<'visible' | 'flying' | 'done'>('visible');
  const [flyTarget, setFlyTarget] = useState<{ x: number; y: number } | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    let rafHandle: number | undefined;
    const timer = setTimeout(() => {
      if (prefersReducedMotion) {
        // Simple fade out
        toast.dismiss(toastId);
        return;
      }

      // Find the download icon position before animating
      const iconEl = document.querySelector(
        '[data-testid="global-download-icon"]'
      );
      const toastEl = containerRef.current;

      if (iconEl && toastEl) {
        const iconRect = iconEl.getBoundingClientRect();
        const toastRect = toastEl.getBoundingClientRect();

        setFlyTarget({
          x:
            iconRect.left +
            iconRect.width / 2 -
            (toastRect.left + toastRect.width / 2),
          y:
            iconRect.top +
            iconRect.height / 2 -
            (toastRect.top + toastRect.height / 2),
        });
        // Use rAF to ensure flyTarget state has been applied before triggering the animation
        rafHandle = requestAnimationFrame(() => {
          setPhase('flying');
        });
      } else {
        setPhase('flying');
      }
    }, 4000);

    return () => {
      clearTimeout(timer);
      if (rafHandle !== undefined) cancelAnimationFrame(rafHandle);
    };
  }, [toastId, prefersReducedMotion]);

  useEffect(() => {
    if (phase === 'flying') {
      const dismissTimer = setTimeout(() => {
        toast.dismiss(toastId);
      }, 500);

      return () => clearTimeout(dismissTimer);
    }
  }, [phase, toastId]);

  const flyVariants: Variants = prefersReducedMotion
    ? {
        visible: { opacity: 1 },
        flying: { opacity: 0, transition: { duration: 0.3 } },
      }
    : {
        visible: { opacity: 1, scale: 1, x: 0, y: 0 },
        flying: {
          opacity: 0,
          scale: 0.3,
          x: flyTarget?.x ?? 0,
          y: flyTarget?.y ?? 0,
          transition: { duration: 0.45, ease: 'easeInOut' },
        },
      };

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          ref={containerRef}
          variants={flyVariants}
          initial="visible"
          animate={phase === 'flying' ? 'flying' : 'visible'}
          className="border-chariot-stroke bg-chariot-layer1 flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg"
        >
          <EntityIcon type={entityType} />
          <div className="flex flex-col">
            <p className="text-chariot-text-primary text-sm font-medium">
              Exporting {getEntityLabel(entityType)}...
            </p>
            <p className="text-chariot-text-secondary text-xs">
              Your file will be ready shortly.
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Trigger an export toast notification.
 * Shows an animated toast that flies toward the download icon after 4 seconds.
 */
export function triggerExportToast(entityType: ExportType): void {
  toast.custom(
    toastId => <ExportToast entityType={entityType} toastId={toastId} />,
    {
      duration: Infinity, // Managed by internal dismiss logic
    }
  );
}
